import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import type {
  CodingBatchDetailResponse,
  CodingBatchPageResponse,
} from '../types/codingBatches'
import { extractErrorMessage } from '../utils/apitools'
import { batchesHaveActiveRows } from '../utils/codingBatches'

/**
 * A pipeline's coding batches: one PAGE of the list, and one batch's
 * drill-down.
 *
 * Both endpoints changed shape in this pass (bare array → envelope, bare row →
 * envelope) and both are read here for the first time — the previous
 * prototypes were never wired to anything.
 *
 * Polling is gated twice over: `enabled` for the mount (a section nobody is
 * looking at must not poll) and, for the list, on the page itself — batches are
 * historical facts the moment they settle, so a settled page stops refetching.
 */

export interface UseCodingBatchesResult {
  data: CodingBatchPageResponse | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

/** The house cadence for a page with work still on it. */
export const BATCHES_POLL_SECONDS = 5

export const useCodingBatchesPaged = (
  customer: string,
  job: string,
  pipelineId: string | undefined,
  page: number,
  rowsPerPage: number,
  enabled = true,
): UseCodingBatchesResult => {
  const query = useQuery<CodingBatchPageResponse, Error>({
    queryKey: ['autocoder', customer, job, 'batches', { pipelineId, page, rowsPerPage }],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
      })
      try {
        const response = await axios.get<CodingBatchPageResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/pipelines/${pipelineId}/batches?${params.toString()}`,
        )
        return response.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: enabled && !!customer && !!job && !!pipelineId,
    // Only while something on THIS page is pending or running. A page of
    // finished batches never changes again.
    refetchInterval: query => (
      batchesHaveActiveRows(query.state.data?.batches || [])
        ? BATCHES_POLL_SECONDS * 1000
        : false
    ),
    refetchIntervalInBackground: false,
    staleTime: 3 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? extractErrorMessage(query.error) : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

/**
 * The same page, JOB-WIDE: every batch this job has sent to a model, whichever
 * pipeline sent it.
 *
 * Same envelope and the same polling rule as the pipeline-scoped hook above —
 * a settled page is history and must not be re-fetched — because it is the same
 * table read through a wider scope. Only the URL differs.
 */
export const useJobCodingBatchesPaged = (
  customer: string,
  job: string,
  page: number,
  rowsPerPage: number,
  enabled = true,
): UseCodingBatchesResult => {
  const query = useQuery<CodingBatchPageResponse, Error>({
    queryKey: ['autocoder', customer, job, 'batches', 'job-wide', { page, rowsPerPage }],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
      })
      try {
        const response = await axios.get<CodingBatchPageResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/batches?${params.toString()}`,
        )
        return response.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: enabled && !!customer && !!job,
    refetchInterval: query => (
      batchesHaveActiveRows(query.state.data?.batches || [])
        ? BATCHES_POLL_SECONDS * 1000
        : false
    ),
    refetchIntervalInBackground: false,
    staleTime: 3 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? extractErrorMessage(query.error) : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

export interface UseCodingBatchDetailResult {
  data: CodingBatchDetailResponse | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
}

/**
 * One batch's drill-down. The prompts and response are realm-admin only and are
 * stripped SERVER-side, so nothing here checks a role: the dialog renders those
 * sections when the keys are present, which is the only honest test of whether
 * this caller is allowed to see them.
 *
 * Polls while the batch is unsettled — a drill-down opened on a running batch
 * is exactly where someone watches its phases move.
 */
export const useCodingBatchDetail = (
  customer: string,
  job: string,
  batchId: string | undefined,
): UseCodingBatchDetailResult => {
  const query = useQuery<CodingBatchDetailResponse, Error>({
    queryKey: ['autocoder', customer, job, 'batchDetail', batchId],
    queryFn: async () => {
      try {
        const response = await axios.get<CodingBatchDetailResponse>(
          `${API_BASE_URL}/autocoder/${customer}/${job}/batches/${batchId}`,
        )
        return response.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    enabled: !!customer && !!job && !!batchId,
    refetchInterval: query => {
      const state = query.state.data?.batch?.state
      return state === 'pending' || state === 'running' ? BATCHES_POLL_SECONDS * 1000 : false
    },
    refetchIntervalInBackground: false,
    staleTime: 3 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? extractErrorMessage(query.error) : null,
  }
}

export default useCodingBatchesPaged
